/**
 *
 * DashboardFilters
 *
 */

import React, { memo, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import moment from 'moment';
import filter from 'lodash/filter';
import {
  DateFilter,
  filterPopoverTrigger,
  Conditional,
  Alert,
  Cross,
  Space,
} from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import {
  FILTERS_BY_NAVIGATION,
  DEFAULT_DATE_OPTIONS,
  CHECKBOX,
  DROPDOWN,
  SEARCH,
  DATE_FILTER_RANGE_SIZE,
  REPORTS,
  PAYMENT_LINKS,
  PAYMENT_FORMS,
  SOFTPOS_SEARCH_ITEMS,
  ALL,
  PAYMENTS,
  ROUTER_ANALYTICS,
  DEFAULT_DATE_TIME_PAYLOAD_FORMAT,
  FILTER_MAPPER,
  ROUTER,
  FLOW_WISE_RECON_REPORT,
  ORDERS,
  IMPORT_TRANSACTIONS,
  SETTLEMENTS,
  STANDARD,
  PAYMENT_VERIFICATION,
  DOWNLOAD_REPORT,
  CUSTOM_DATE_RANGE,
  OFFERSV2,
} from 'utils/constants';
import {
  getFilterCount,
  prepareFiltersForDisplayObj,
  isEPOSApproved,
  isSubscriptionEnabled,
  // setItemsInLocalStorage,
  // getItemFromLocalStorage,
} from 'utils/common';
import { MERCHANT_TYPE, useMerchantType } from 'utils/v2/common';
import toUpper from 'lodash/toUpper';
import { getTableConfig } from 'pageConfig';
import LocalStorageDB from '../../utils/localStorageDb';
import Analytics from '../../utils/analytics';
import makeSelectDashboardFilters from './selectors';
import reducer from './reducer';
import saga from './saga';

import FiltersByNavigation from '../../components/FiltersByNavigation';
import AppliedFilterList from '../../components/AppliedFilterList';

import { MerchantContext } from '../../contexts/MerchantContext';
import { modifyDefaultDateOptions } from './helpers';
import {
  DISABLE_CHECKBOX,
  EASY_SPLIT,
  EASY_SPLIT_ALL,
  NEW_FLOW,
  OLD_FLOW,
} from './constants';

// Create the db instance outside the component
const db = new LocalStorageDB('pageFilters');

export function DashboardFilters(props) {
  useInjectReducer({ key: 'dashboardFilters', reducer });
  useInjectSaga({ key: 'dashboardFilters', saga });

  const {
    easySplitEnabled,
    isFlowwiseActive,
    captureConfig,
    isMerchantImportEnabled,
  } = useContext(MerchantContext);
  const merchantType = useMerchantType();
  const isOCCActivated = merchantType !== MERCHANT_TYPE.PG;

  const [searchString, resetSearchString] = useState('');

  const {
    menuSelected,
    filters,
    activeNavigation,
    filtersForDisplay = [],
    resetFilterFn,
    updateFilterFn,
    hideDateFilter = false,
    filterItems = {},
    hideFilters = false,
    resetPageFn = () => null,
    maxDate,
    minDate,
    isStartTimeEqualsNowFn,
    dateOptions,
    rangeSize,
    singleSelect,
    children,
    customDateRange = true,
    buttonDisplayText = 'Search & Filter',
    searchOnly = false,
    incrementalVendorOnboarding = false,
    enableSaveFilters = false,
    enableBorder = false,
    customDateFilterAlert,
  } = props;

  const { dateFilters = {}, appliedFilters } = filters;
  const [selectedFilters, resetSelectedFilters] = useState();
  const [regexTest, setRegexTest] = useState(true);
  const [showAlert, setShowAlert] = useState(false);

  const defaultDateOptions = dateOptions || [...DEFAULT_DATE_OPTIONS];

  const localStorageKey = toUpper(`${menuSelected}_${activeNavigation}`);

  modifyDefaultDateOptions(
    menuSelected,
    activeNavigation,
    defaultDateOptions,
    filters,
  );

  useEffect(() => {
    if (appliedFilters) {
      const { startDate, endDate, ...f } = appliedFilters;
      resetSelectedFilters({
        ...f,
      });
    }
  }, [filters, activeNavigation]);

  const hideDateFilterFlag =
    hideDateFilter ||
    !appliedFilters ||
    (!appliedFilters.startDate &&
      !appliedFilters.endDate &&
      !filters.dateFilters);

  const computeFiltersByNavigationObj = () => {
    let resultant = FILTERS_BY_NAVIGATION?.[menuSelected]?.[activeNavigation];
    resultant = resultant ? JSON.parse(JSON.stringify(resultant)) : {};

    if (menuSelected === REPORTS) {
      // if Easy Split is enabled then add vendor reports to the filters
      if (easySplitEnabled) {
        resultant = JSON.parse(
          JSON.stringify(FILTERS_BY_NAVIGATION[menuSelected].VENDOR_REPORTS),
        );
      }
      if (isSubscriptionEnabled()) {
        resultant.FILTER_ITEMS.type.items = resultant.FILTER_ITEMS.type.items.concat(
          FILTERS_BY_NAVIGATION[menuSelected].SUB_REPORTS.FILTER_ITEMS.type
            .items,
        );
      }
      if (!isFlowwiseActive) {
        resultant.FILTER_ITEMS.type.items = filter(
          resultant.FILTER_ITEMS.type.items,
          item => item.value !== FLOW_WISE_RECON_REPORT,
        );
      }

      if (isMerchantImportEnabled && activeNavigation === DOWNLOAD_REPORT) {
        const tableConfig = getTableConfig({
          page: REPORTS,
          isImportEnabled: isMerchantImportEnabled,
        });
        resultant = tableConfig?.[activeNavigation];
        resultant = resultant ? JSON.parse(JSON.stringify(resultant)) : {};
      }
    } else if (menuSelected === PAYMENTS) {
      const tableConfig = getTableConfig({
        page: IMPORT_TRANSACTIONS.PAYMENTS,
        isImportEnabled: isMerchantImportEnabled,
      });

      resultant = tableConfig?.[activeNavigation];
      resultant = resultant ? JSON.parse(JSON.stringify(resultant)) : {};

      if (activeNavigation === ALL && resultant.FILTER_ITEMS) {
        // if router is not enabled remove it from the filter items
        if (isFlowwiseActive) {
          // add the pgUsed filter items here
          resultant.FILTER_ITEMS = {
            ...filterItems,
            ...resultant.FILTER_ITEMS,
          };
        }
        // if capture config is not enabled remove it from the filter items
        if (!captureConfig) delete resultant.FILTER_ITEMS.isCaptured;
      }
      // if activeNavigation is Payments and softEPOS is enabled then add options to the search items
      if (
        activeNavigation === ALL &&
        isEPOSApproved() &&
        !isMerchantImportEnabled
      ) {
        resultant.SEARCH_ITEMS.push(...SOFTPOS_SEARCH_ITEMS);
      }
    } else if (menuSelected === ROUTER_ANALYTICS) {
      resultant.FILTER_ITEMS = {
        ...resultant.FILTER_ITEMS,
        ...filterItems,
      };
      if (!isFlowwiseActive) delete resultant.FILTER_ITEMS.pg;
    } else if (menuSelected === ROUTER) {
      resultant.FILTER_ITEMS = {
        ...filterItems,
        ...resultant.FILTER_ITEMS,
      };
    } else if (menuSelected === 'ROUTER_ANALYTICS_V2') {
      resultant.FILTER_ITEMS = {
        ...resultant.FILTER_ITEMS,
        ...filterItems,
      };
    } else if (
      menuSelected === EASY_SPLIT &&
      activeNavigation === EASY_SPLIT_ALL
    ) {
      const easySplitFlow = incrementalVendorOnboarding ? NEW_FLOW : OLD_FLOW;
      resultant =
        FILTERS_BY_NAVIGATION[menuSelected][activeNavigation][easySplitFlow];
    } else if (menuSelected === ORDERS) {
      const tableConfig = getTableConfig({
        page: IMPORT_TRANSACTIONS.ORDERS,
        isImportEnabled: isMerchantImportEnabled,
      });

      resultant = tableConfig?.[activeNavigation];
      resultant = resultant ? JSON.parse(JSON.stringify(resultant)) : {};
    } else if (menuSelected === SETTLEMENTS && activeNavigation === STANDARD) {
      const tableConfig = getTableConfig({
        page: IMPORT_TRANSACTIONS.SETTLEMENTS,
        isImportEnabled: isMerchantImportEnabled,
      });

      resultant = tableConfig?.[activeNavigation];
      resultant = resultant ? JSON.parse(JSON.stringify(resultant)) : {};
    } else if (menuSelected === PAYMENT_VERIFICATION) {
      const tableConfig = getTableConfig({
        page: IMPORT_TRANSACTIONS.PAYMENT_VERIFICATION,
        isImportEnabled: isMerchantImportEnabled,
      });

      resultant = tableConfig?.[activeNavigation];
      resultant = resultant ? JSON.parse(JSON.stringify(resultant)) : {};
    }
    // Scoped by menuSelected: only the OffersV2 flow reaches this. V1 Offers
    // passes menuSelected=OFFERS, so its filter set is untouched.
    if (menuSelected === OFFERSV2) {
      if (isOCCActivated) {
        if (resultant?.FILTER_ITEMS?.paymentMethod?.items) {
          resultant.FILTER_ITEMS.paymentMethod.items = {
            ...resultant.FILTER_ITEMS.paymentMethod.items,
            PREPAID: 'Prepaid',
          };
        }
      }
      if (
        merchantType !== MERCHANT_TYPE.OCC &&
        resultant?.FILTER_ITEMS?.type?.items
      ) {
        delete resultant.FILTER_ITEMS.type.items.GIFT;
      }
      if (
        merchantType !== MERCHANT_TYPE.OCC &&
        resultant?.FILTER_ITEMS?.platform?.items
      ) {
        delete resultant.FILTER_ITEMS.platform.items.SHOPIFY;
      }
    }

    // check if filters are present
    // if yes -> put it at the top as saved filters with type as radio or dropdown
    // check if radio is already present else use dropdown only
    if (enableSaveFilters) {
      const savedFilters = db.get(localStorageKey);

      if (savedFilters) {
        const filterNames = Object.keys(savedFilters);
        const savedFilterItems = filterNames.map(name => ({
          key: name,
          value: name,
          text: name,
        }));
        // added Saved Filters to the filter items
        resultant.FILTER_ITEMS = {
          saved: {
            displayText: `Saved (${savedFilterItems.length} available)`,
            type: DROPDOWN,
            items: savedFilterItems,
          },
          ...resultant.FILTER_ITEMS,
        };
      }
    }

    return resultant;
  };

  const filtersByNavigationObj = computeFiltersByNavigationObj();

  const handleAnalyticsForFilters = ({
    filtersObj = '',
    selectedRange = null,
  }) => {
    const updatedFilters = JSON.parse(JSON.stringify(filtersObj));
    if (updatedFilters && updatedFilters.search) delete updatedFilters.search;
    const updatedFiltersArr = Object.entries(updatedFilters).filter(item => {
      if (Array.isArray(item[1])) return item[1].length;
      return item[1];
    });

    if (selectedRange?.displayText === CUSTOM_DATE_RANGE) {
      Analytics.trackEventWithNewNomenclature({
        eventName: 'Table_Date_Filters:Apply',
        eventProperties: {
          startDate: selectedRange.range[0],
          endDate: selectedRange.range[1],
          type: CUSTOM_DATE_RANGE,
        },
      });
      return;
    }

    switch (menuSelected) {
      case PAYMENT_LINKS: {
        const computeSearchType = value => {
          if (value === 'customerPhone') return 'phone';
          if (value === 'customerEmail') return 'email';
          if (value === 'referenceId') return 'linkid';
          if (value === 'id') return 'cflinkid';
          if (value === 'batchId') return 'batchid';
          if (value === 'orderId') return 'orderid';
          return '';
        };
        if (
          (filtersObj.search && filtersObj.search.value) ||
          filtersObj.orderStatus
        ) {
          Analytics.trackEventWithNewNomenclature({
            eventName: 'Table_Filters:Apply',
            eventProperties: {
              search: filtersObj.search
                ? computeSearchType(filtersObj.search.type)
                : '',
              status: filtersObj.orderStatus ? filtersObj.orderStatus : [],
            },
          });
        }
        break;
      }

      case PAYMENT_FORMS: {
        const computeSearchType = value => {
          if (value === 'customerPhone') return 'phone';
          if (value === 'customerEmail') return 'email';
          if (value === 'referenceId' || value === 'paymentRequestReferenceId')
            return 'formid';
          if (value === 'id' || value === 'paymentRequestId') return 'cfformid';
          if (value === 'form_url') return 'formurl';
          if (value === 'orderId') return 'orderid';
          return '';
        };
        if (
          (filtersObj.search && filtersObj.search.value) ||
          filtersObj.orderStatus
        ) {
          Analytics.trackEventWithNewNomenclature({
            eventName: 'Table_Filters:Apply',
            eventProperties: {
              search: filtersObj.search
                ? computeSearchType(filtersObj.search.type)
                : '',
              status: filtersObj.orderStatus ? filtersObj.orderStatus : [],
            },
          });
        }
        break;
      }

      default: {
        const eventProperties = {};

        if (filtersObj.search && filtersObj.search.type) {
          eventProperties.search = filtersObj.search.type;
        }
        if (updatedFiltersArr.length) {
          eventProperties.filters = Object.fromEntries(updatedFiltersArr);
        }

        Analytics.trackEventWithNewNomenclature({
          eventName: 'Table_Filters:Apply',
          eventProperties,
        });
        break;
      }
    }
  };

  const onDateRangeSelect = selectedRange => {
    const isStartTimeEqualNow =
      isStartTimeEqualsNowFn && isStartTimeEqualsNowFn(selectedRange);

    if (selectedRange.range && selectedRange.range.length > 1) {
      updateFilterFn({
        appliedFilters: {
          ...appliedFilters,
          startDate: moment(selectedRange.range[0]).format(
            !isStartTimeEqualNow
              ? 'YYYY-MM-DD 00:00:00'
              : DEFAULT_DATE_TIME_PAYLOAD_FORMAT,
          ),
          endDate: moment(selectedRange.range[1]).format(
            DEFAULT_DATE_TIME_PAYLOAD_FORMAT,
          ),
        },
        filtersForDisplay: prepareFiltersForDisplayObj(
          { ...appliedFilters },
          filtersByNavigationObj,
        ),
        dateFilters: selectedRange,
        activeNavigation,
      });
      resetPageFn(activeNavigation);
      handleAnalyticsForFilters({ selectedRange });
    }
  };

  const prepareFilterObj = () => {
    const result = {
      appliedFilters: {
        ...selectedFilters,
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
      },
      filtersForDisplay: prepareFiltersForDisplayObj(
        { ...selectedFilters },
        filtersByNavigationObj,
      ),
      applyFilter: true,
      activeNavigation,
    };

    if (
      'saved' in selectedFilters &&
      db.get(localStorageKey) &&
      db.get(localStorageKey)[selectedFilters.saved] &&
      db.get(localStorageKey)[selectedFilters.saved].dateFilters
    ) {
      // compute the date filter
      const updatedDateFilters =
        db.get(localStorageKey)[selectedFilters.saved]?.dateFilters ||
        dateFilters;

      // see how to handle the Custom and the standard scenarios
      const { range, displayText } = updatedDateFilters;
      // this might not work for cases when date options are passed in the props
      let updatedRange = range.map(val => moment(val).toDate());

      // find the object in DEFAULT_DATE_OPTIONS and use that range to do the things
      const dateObj = DEFAULT_DATE_OPTIONS.find(
        obj => obj.displayText === displayText,
      );

      if (dateObj) updatedRange = dateObj.range;

      result.dateFilters = { displayText };
      result.dateFilters.range = updatedRange;

      const isStartTimeEqualNow =
        isStartTimeEqualsNowFn && isStartTimeEqualsNowFn(updatedDateFilters);

      result.appliedFilters.startDate = moment(updatedRange[0]).format(
        !isStartTimeEqualNow
          ? 'YYYY-MM-DD 00:00:00'
          : DEFAULT_DATE_TIME_PAYLOAD_FORMAT,
      );
      result.appliedFilters.endDate = moment(updatedRange[1]).format(
        DEFAULT_DATE_TIME_PAYLOAD_FORMAT,
      );
    }
    return result;
  };

  const onFilterApply = filterName => {
    if (singleSelect && modifiedSelectedFilterCount !== 1) {
      setShowAlert(true);
      return;
    }
    setShowAlert(false);

    const result = prepareFilterObj();
    // add the logic of saving the TODAY, LAST_MONTH etc. Date Filter values here
    updateFilterFn(result);
    resetPageFn(activeNavigation);
    handleAnalyticsForFilters({ filtersObj: selectedFilters });

    if (filterName) {
      const lsFiltersValue = db.get(localStorageKey);

      if (lsFiltersValue) {
        // use update here
        db.update(
          Object.assign(lsFiltersValue, {
            [filterName]: { ...result, dateFilters },
          }),
          localStorageKey,
        );
      } else {
        // create a new table for the current location - localStorageKey
        db.create(localStorageKey, {
          [filterName]: { ...result, dateFilters },
        });
      }
    }
  };

  const handleFilterChange = filterObj => (e, data) => {
    let updatedFilters = JSON.parse(JSON.stringify(selectedFilters));
    const {
      key = '',
      value = '',
      type = '',
      subFilters,
      parentFilter,
    } = filterObj;

    // If current selection is saved filter then clear the other filters
    // and update the selection to what the saved filters must be
    if (key === 'saved') {
      // updatedFilters = {};
      const savedFilters = db.get(localStorageKey);
      if (savedFilters[data.value]) {
        const { startDate, endDate, ...rest } = savedFilters[
          data.value
        ].appliedFilters;
        updatedFilters = rest;
      }
    } else if ('saved' in updatedFilters) {
      // else delete the saved filter
      delete updatedFilters.saved;
    }

    switch (type) {
      case CHECKBOX: {
        // base condition
        if (!updatedFilters[key]) updatedFilters[key] = [];
        const index = updatedFilters[key].findIndex(elem => elem === value);
        // check if selected or not
        if (index === -1) updatedFilters[key].push(value);
        else updatedFilters[key].splice(index, 1);

        // handle the subFilter case
        if (subFilters) {
          if (!updatedFilters[subFilters.key])
            updatedFilters[subFilters.key] = [];
          // logic for select / de-select all
          Object.keys(subFilters.items).forEach(k => {
            if (index === -1) {
              // select all
              updatedFilters[subFilters.key].push(k);
            } else {
              // de-select all
              const pos = updatedFilters[subFilters.key].findIndex(
                elem => elem === k,
              );
              updatedFilters[subFilters.key].splice(pos, 1);
            }
          });
        }
        // handle the parentFilter case
        if (parentFilter) {
          if (!updatedFilters[parentFilter.key])
            updatedFilters[parentFilter.key] = [];
          const subFilterPresent = updatedFilters[key].some(
            elem => parentFilter.items[elem],
          );
          if (subFilterPresent) {
            // check if any children exists
            //  if parent is not selected then select the parent
            if (
              !updatedFilters[parentFilter.key].includes(parentFilter.value)
            ) {
              updatedFilters[parentFilter.key].push(parentFilter.value);
            }
          } else {
            // if any children does not exist then remove the parent also
            // remove the parentFilter.value from updatedFilters[parentFilter.key]
            const pos = updatedFilters[parentFilter.key].findIndex(
              elem => elem === parentFilter.value,
            );
            updatedFilters[parentFilter.key].splice(pos, 1);
          }
        }
        break;
      }
      case SEARCH: {
        // base condition
        if (!updatedFilters[type])
          updatedFilters[type] = {
            type: filtersByNavigationObj.SEARCH_ITEMS[0].value,
          };
        // update the value and type
        if (key === DROPDOWN) {
          updatedFilters[type].type = data.value;
        } else {
          updatedFilters[type].value = data.value;
        }
        checkValidSearchInput(updatedFilters);
        break;
      }
      default: {
        updatedFilters[key] = data.value;
      }
    }

    resetSelectedFilters(updatedFilters);
    if (e) e.stopPropagation();
  };

  const handleReset = () => {
    resetSelectedFilters({});
    resetFilterFn(activeNavigation);
    resetPageFn(activeNavigation);
  };

  const removeFilter = ({ key, val, subFilterKey }) => () => {
    const updatedFilters = JSON.parse(JSON.stringify(appliedFilters));

    if (Array.isArray(updatedFilters[key])) {
      updatedFilters[key] = updatedFilters[key].filter(elem => elem !== val);
    } else if (key === 'orderAmount') {
      ['orderAmount', 'startOrderAmount', 'endOrderAmount'].forEach(e => {
        delete updatedFilters[e];
      });
    } else {
      delete updatedFilters[key];
    }
    // remove the sub-filter items
    if (subFilterKey) {
      // exception case, this will be refactored later once the backend gives refactored solution
      if (key === 'paymentMode' && subFilterKey === 'bankName') {
        const { items: paymentModeItems } =
          filtersByNavigationObj.FILTER_ITEMS[key] || {};
        const { items } = paymentModeItems[val];
        if (items) {
          const filteredItems = updatedFilters[subFilterKey].filter(
            k => !items[k],
          );
          if (filteredItems.length > 0) {
            updatedFilters[subFilterKey] = filteredItems;
          } else {
            delete updatedFilters[subFilterKey];
          }
        }
      } else {
        const { items } =
          filtersByNavigationObj.FILTER_ITEMS[subFilterKey] || {};
        if (items) {
          const filteredItems = updatedFilters[subFilterKey].filter(
            k => !items[val].items[k],
          );
          updatedFilters[subFilterKey] = filteredItems;
        } else {
          delete updatedFilters[subFilterKey];
        }
      }
    }

    // check if saved is present in updatedFilters then remove it
    // since on removing directly using cross icon we want to deselect the saved filter
    if ('saved' in updatedFilters) delete updatedFilters.saved;

    updateFilterFn({
      appliedFilters: {
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
        ...updatedFilters,
      },
      filtersForDisplay: prepareFiltersForDisplayObj(
        { ...updatedFilters },
        filtersByNavigationObj,
      ),
      activeNavigation,
    });

    resetPageFn(activeNavigation);
  };

  const onSearchInputChange = e => {
    resetSearchString(e.target.value || '');
  };

  const checkValidSearchInput = updatedFilters => {
    const { type, value } =
      updatedFilters && updatedFilters.search ? updatedFilters.search : '';
    filtersByNavigationObj.SEARCH_ITEMS.forEach(item => {
      if (item.value === type) {
        if (value) {
          const regex = new RegExp(item.inputPattern);
          setRegexTest(regex.test(value));
        } else {
          setRegexTest(true);
        }
      }
    });
  };

  const isInvalidOrderAmount =
    selectedFilters &&
    Object.keys(FILTER_MAPPER).reduce(
      (acc, curr) =>
        acc ||
        (selectedFilters[curr] &&
          selectedFilters[curr] === 'IS_BETWEEN' &&
          ((selectedFilters[FILTER_MAPPER[curr].end] &&
            Number(selectedFilters[FILTER_MAPPER[curr].end]) <
              Number(selectedFilters[FILTER_MAPPER[curr].start])) ||
            (selectedFilters[FILTER_MAPPER[curr].start] &&
              !selectedFilters[FILTER_MAPPER[curr].end]))),
      false,
    );

  const modifiedAppliedFilterCount =
    isInvalidOrderAmount || !regexTest ? 0 : filtersForDisplay.length;

  const modifiedSelectedFilterCount =
    isInvalidOrderAmount || !regexTest
      ? 0
      : getFilterCount(selectedFilters || {});

  return (
    <div>
      <Conditional if={showAlert}>
        <Alert className="mb-2" size="md" type="warning" compact bordered>
          Please select only one filter.
          <Cross
            size="md"
            className="ml-3"
            onClick={() => setShowAlert(false)}
            data-event-name="Icon_Close"
          />
        </Alert>
      </Conditional>
      <div className="filter-row">
        <div
          className="filter-group"
          style={{ flexDirection: children ? 'column' : 'row' }}
        >
          {hideDateFilterFlag ? null : (
            <>
              <div className="date-filter-div">
                <DateFilter
                  rangeSize={rangeSize || DATE_FILTER_RANGE_SIZE}
                  value={dateFilters}
                  onSelect={onDateRangeSelect}
                  trigger={filterPopoverTrigger}
                  options={defaultDateOptions}
                  max={maxDate}
                  min={minDate}
                  custom={customDateRange}
                  customDateFilterAlert={customDateFilterAlert}
                />
              </div>
            </>
          )}

          {!!children && <>{children}</>}
          <Space alignItems="end">
            {hideFilters ? null : (
              <div
                className={
                  enableBorder ? 'filter-popup-border' : 'filter-popup'
                }
              >
                <FiltersByNavigation
                  onFilterApply={onFilterApply}
                  handleReset={handleReset}
                  appliedFiltersCount={modifiedAppliedFilterCount}
                  filtersByNavigation={filtersByNavigationObj}
                  selectedFilters={selectedFilters}
                  selectedFiltersCount={modifiedSelectedFilterCount}
                  handleFilterChange={handleFilterChange}
                  onSearchInputChange={onSearchInputChange}
                  searchString={searchString}
                  isError={isInvalidOrderAmount}
                  regexTest={regexTest}
                  disableCheckbox={DISABLE_CHECKBOX.includes(menuSelected)}
                  menuSelected={menuSelected}
                  singleSelect={singleSelect}
                  buttonDisplayText={buttonDisplayText}
                  searchOnly={searchOnly}
                  enableSaveFilters={enableSaveFilters}
                />
              </div>
            )}
            {filtersForDisplay.length > 0 && (
              <AppliedFilterList
                removeFilter={removeFilter}
                filtersForDislay={filtersForDisplay}
              />
            )}
          </Space>
        </div>
      </div>
    </div>
  );
}

DashboardFilters.propTypes = {
  menuSelected: PropTypes.string,

  filters: PropTypes.object,
  activeNavigation: PropTypes.string,
  filtersForDisplay: PropTypes.array,
  children: PropTypes.node,
  resetFilterFn: PropTypes.func,
  updateFilterFn: PropTypes.func,
  resetPageFn: PropTypes.func,
  hideFilters: PropTypes.bool,
  filterItems: PropTypes.object,
  hideDateFilter: PropTypes.bool,
  maxDate: PropTypes.object,
  minDate: PropTypes.object,
  isStartTimeEqualsNowFn: PropTypes.func,
  dateOptions: PropTypes.object,
  rangeSize: PropTypes.number,
  singleSelect: PropTypes.bool,
  customDateRange: PropTypes.bool,
  buttonDisplayText: PropTypes.string,
  searchOnly: PropTypes.bool,
  incrementalVendorOnboarding: PropTypes.bool,
  enableSaveFilters: PropTypes.bool,
  enableBorder: PropTypes.bool,
  customDateFilterAlert: PropTypes.node,
};

const mapStateToProps = createStructuredSelector({
  dashboardFilters: makeSelectDashboardFilters(),
});

function mapDispatchToProps() {
  return {};
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(DashboardFilters);
